import { useEffect, useMemo, useState } from "react";
import { glyphs } from "../../../../../Styles";
import { NewButton, GenericDeviceTable, ResetTableModal, ResetTableClearQueueModal } from "../../../../../Components";
import { ButtonTypes, ListData_ValueTypes, LC_TABLE_IDS } from "../../../../../Definitions";
import { LC_TERMINAL, LCTerminalArgs, LCTerminalResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/lcTerminal";
import { useLazyQuery } from "@apollo/client";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";

const columnsData = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "deviceId", accessor: "deviceId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "port", accessor: "port", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "parentDeviceId", accessor: "parentDeviceId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "parentPort", accessor: "parentPort", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "nextDeviceId", accessor: "nextDeviceId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "nextPort", accessor: "nextPort", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "terminalType", accessor: "terminalType", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "timeAct", accessor: "timeAct", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "val1_min", accessor: "val1_min", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "val1_max", accessor: "val1_max", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "val1_type", accessor: "val1_type", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "val2_type", accessor: "val2_type", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "val2_min", accessor: "val2_min", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "val2_max", accessor: "val2_max", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "extra", accessor: "extra", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "programmed", accessor: "programmed", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "sensor_p", accessor: "sensor_p", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "consumptionDeviceId", accessor: "consumptionDeviceId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "consumptionPort", accessor: "consumptionPort", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "correction", accessor: "correction", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "limit", accessor: "limit", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "activations", accessor: "activations", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "periodecomp", accessor: "periodecomp", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "extra_pause", accessor: "extra_pause", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "last_run", accessor: "last_run", type: ListData_ValueTypes.Date, sort: true },
	{ headerValue: "milestoneId", accessor: "milestoneId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "feedcurveId", accessor: "feedcurveId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "milestoneDay", accessor: "milestoneDay", type: ListData_ValueTypes.Date, sort: true },
	{ headerValue: "componentId", accessor: "componentId", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "skipCnt", accessor: "skipCnt", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "skipTime", accessor: "skipTime", type: ListData_ValueTypes.Date, sort: true },
]

export default function TerminalDataTable() {
	const [isResetTableModalOpen, setIsResetTableModalOpen] = useState<boolean>(false);
	const [isResetTableClearQueueModalOpen, setIsResetTableClearQueueModalOpen] = useState<boolean>(false);

	const { state: connectionState } = useAicoConnection();

	const [fetchData, {data, loading, error }] = useLazyQuery<LCTerminalResponse, LCTerminalArgs>(LC_TERMINAL, { fetchPolicy: "network-only" });
	
	useEffect(() => {
		if (connectionState.deviceConnection) {
			fetchData({variables: connectionState.deviceConnection});
		}
	}, [connectionState.deviceConnection]);

	const tableData = useMemo(() => {
		return data?.controller.lcTerminal?.map((e, i) => ({
			...e,
			id: i,
			last_run: e.last_run ? new Date(e.last_run * 1000) : undefined,
			milestoneDay: e.milestoneDay ? new Date(e.milestoneDay * 1000) : undefined,
			skipTime: e.skipTime ? new Date(e.skipTime * 1000) : undefined
		})) ?? [];
	}, [data]);

	return (
		<>
			<GenericDeviceTable
				title="Terminals"
				columnsData={columnsData}
				data={tableData}
				loading={loading}
				error={error}
				onFetch={() => connectionState.deviceConnection && fetchData({ variables: connectionState.deviceConnection })}
				tabButtons={[
					<NewButton
						key="Reset table"
						content="Reset Table"
						startEnhancer={glyphs.ArrowReset({})}
						onClick={() => {
							setIsResetTableModalOpen(true);
						}}
						buttonType={ButtonTypes.tableButton}
						disabled={!connectionState.deviceConnection}
					/>,
					<NewButton
						key="Reset & clear"
						content="Reset & Clear"
						startEnhancer={glyphs.ArrowReset({})}
						onClick={() => {
							setIsResetTableClearQueueModalOpen(true);
						}}
						buttonType={ButtonTypes.tableButton}
						disabled={!connectionState.deviceConnection}
					/>
				]}
			/>
			{connectionState.deviceConnection && isResetTableModalOpen && 
				<ResetTableModal
					closeModal={()=>{ setIsResetTableModalOpen(false) }}
					onSuccess={() => fetchData({ variables: connectionState.deviceConnection })}
					tableId={LC_TABLE_IDS.terminals}
				/>
			}
			{connectionState.deviceConnection && isResetTableClearQueueModalOpen &&
				<ResetTableClearQueueModal
					closeModal={()=>{ setIsResetTableClearQueueModalOpen(false) }}
					onSuccess={() => fetchData({ variables: connectionState.deviceConnection })}
					tableId={LC_TABLE_IDS.terminals}
				/>
			}
		</>
	)
}